import { useNavigate } from 'react-router-dom';
import { useFontSize, type FontSizeOption } from '@shared/hooks/use-font-size';

interface FontOptionItem {
  id: FontSizeOption;
  label: string;
  sub: string;
  badgeSizeClass: string;
}

const FONT_OPTIONS: FontOptionItem[] = [
  {
    id: 'small',
    label: 'Small',
    sub: 'Default size',
    badgeSizeClass: 'text-xs',
  },
  {
    id: 'normal',
    label: 'Medium (Default)',
    sub: 'Recommended for most users',
    badgeSizeClass: 'text-sm font-semibold',
  },
  {
    id: 'large',
    label: 'Large',
    sub: 'Better for easier reading',
    badgeSizeClass: 'text-base font-bold',
  },
  {
    id: 'xlarge',
    label: 'Extra Large',
    sub: 'Best for maximum readability',
    badgeSizeClass: 'text-lg font-black',
  },
];

export function FontPreferencePage() {
  const navigate = useNavigate();
  const { fontSize, setFontSize } = useFontSize();

  return (
    <div className="mx-auto max-w-lg px-4 py-6 sm:px-6 pb-24">
      {/* Header with back button (Mobile Only) */}
      <div className="md:hidden flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => navigate('/client/menu')}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 hover:text-brand-800 bg-white border border-gray-200 hover:border-brand-300 px-3 py-1.5 rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer select-none"
          aria-label="Back to Menu"
        >
          <svg
            className="w-4 h-4 text-brand-700"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          <span>Back to Menu</span>
        </button>
      </div>

      <h1 className="mt-4 text-xl font-extrabold text-gray-900 sm:text-2xl">Font Preference</h1>

      <p className="mt-4 text-sm text-gray-600 leading-relaxed">
        Choose your preferred font size for better readability across the app.
      </p>

      {/* Font Size Option Cards */}
      <div className="mt-6 space-y-3">
        {FONT_OPTIONS.map((opt) => {
          const isSelected = fontSize === opt.id;
          return (
            <div
              key={opt.id}
              onClick={() => setFontSize(opt.id)}
              className={`flex cursor-pointer items-center justify-between rounded-2xl border p-4 transition-all select-none ${
                isSelected
                  ? 'border-brand-600 bg-brand-50/40 ring-1 ring-brand-500/30 shadow-xs'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
              }`}
            >
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors ${
                    isSelected ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-600'
                  } ${opt.badgeSizeClass}`}
                >
                  Aa
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-gray-900 sm:text-sm">{opt.label}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{opt.sub}</p>
                </div>
              </div>

              {/* Radio button circle */}
              <div className="ml-3 shrink-0">
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full border transition-all ${
                    isSelected ? 'border-brand-600 bg-white' : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected ? <div className="h-3 w-3 rounded-full bg-brand-700" /> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Live Preview Box */}
      <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50/70 p-5">
        <span className="text-xs font-bold uppercase tracking-wider text-brand-700">Preview</span>
        <p className="mt-2 text-base text-gray-800 leading-relaxed font-medium">
          This is how text will look like with the selected size.
        </p>
      </div>

      {/* Info notice banner */}
      <div className="mt-6 flex items-center gap-2.5 rounded-xl border border-brand-200/80 bg-brand-50/60 p-3.5 text-xs font-semibold text-brand-800">
        <svg
          className="w-4 h-4 shrink-0 text-brand-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>You can change this anytime in settings.</span>
      </div>
    </div>
  );
}
